import { NSARouterMakerResult } from "namirasoft-account-react";
import { ChatRow, EntityType, RunRow, RunStatus } from "namirasoft-expert";
import { NSBox, NSLoading, NSTitle } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ExpertRouterProps } from "../ExpertRouterProps";
import { ExpertRoutes } from "../ExpertRoutes";
import { ChatPageInner } from "../pages/ChatPage";
import Styles from "./ChatAgent.module.css";
import MentionTextArea, { Mention } from "./MentionTextArea";
import RunAgent from "./RunAgent";
import ServerSelection from "./ServerSelection";

interface ChatAgentProps extends NSARouterMakerResult, ExpertRouterProps
{
    agent_id: string;
    chat_id: string | null;
    runs: RunRow[];
    onRunChanged?: (run: RunRow) => void;
    onChatChanged?: (chat: ChatRow) => void;
}

interface ChatAgentState
{
    runs: RunRow[];
    submitting: boolean;
}

class ChatAgent extends Component<ChatAgentProps, ChatAgentState>
{
    private MentionTextArea_Ref = createRef<MentionTextArea>();
    private initialRunIds: Set<string>;

    constructor(props: ChatAgentProps)
    {
        super(props);
        const sortedRuns = ChatPageInner.sortRuns(props.runs);
        this.state = {
            runs: sortedRuns,
            submitting: false
        };
        this.initialRunIds = new Set(sortedRuns.map(r => r.id));
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    private hasInProgressRun(): boolean
    {
        return this.state.runs.some(r =>
            r.status === RunStatus.Pending ||
            r.status === RunStatus.Running ||
            r.status === RunStatus.Waiting
        );
    }

    private buildEntityArgs(mentions: Mention[])
    {
        const hasAny = mentions.length > 0;
        const has = (t: EntityType) => mentions.some(m => m.entity_type === t);
        const ids = (t: EntityType) => mentions.filter(m => m.entity_type === t && m.entity_id).map(m => m.entity_id!);

        return {
            use_entity_cloud: hasAny ? has(EntityType.Cloud) : true,
            use_entity_server: hasAny ? has(EntityType.Server) : true,
            use_entity_kubernetes: hasAny ? has(EntityType.Kubernetes) : true,
            use_entity_container: hasAny ? has(EntityType.Container) : true,
            use_entity_database: hasAny ? has(EntityType.Database) : true,
            use_entity_cache: hasAny ? has(EntityType.Cache) : true,
            use_entity_messaging: hasAny ? has(EntityType.Messaging) : true,
            use_entity_streaming: hasAny ? has(EntityType.Streaming) : true,
            use_entity_metric: hasAny ? has(EntityType.Metric) : true,
            use_ids_cloud: ids(EntityType.Cloud),
            use_ids_server: ids(EntityType.Server),
            use_ids_kubernetes: ids(EntityType.Kubernetes),
            use_ids_container: ids(EntityType.Container),
            use_ids_database: ids(EntityType.Database),
            use_ids_cache: ids(EntityType.Cache),
            use_ids_messaging: ids(EntityType.Messaging),
            use_ids_streaming: ids(EntityType.Streaming),
            use_ids_metric: ids(EntityType.Metric),
        };
    }

    private async handleSubmit()
    {
        let value: string;
        let mentions: Mention[];
        try
        {
            const box = this.MentionTextArea_Ref.current;
            const payload = box?.getValue();
            const text = payload?.text?.trim();
            if (!text)
                return;
            value = text;
            mentions = payload?.mentions ?? [];
        }
        catch (error: any)
        {
            // Input validation errors are not reported by the server, so notify here.
            this.props.notifier.onError(error);
            return;
        }

        const entityArgs = this.buildEntityArgs(mentions);
        this.setState({ submitting: true });
        try
        {
            if (this.props.chat_id)
            {
                const run = await this.props.servers.expert.agent.Continue({
                    chat_id: this.props.chat_id,
                    message_user: value,
                    ...entityArgs,
                });
                this.setState({
                    runs: ChatPageInner.sortRuns([...this.state.runs, run]),
                    submitting: false,
                });
                this.props.onRunChanged?.(run);
                this.MentionTextArea_Ref.current?.clear();
            }
            else
            {
                let result = await this.props.servers.expert.agent.Start({
                    agent_id: this.props.agent_id,
                    incident_id: null,
                    message_user: value,
                    ...entityArgs,
                });
                if (result.chat_id)
                    this.props.url.redirect(ExpertRoutes.Chat_AgentContinue(this.props.agent_id, result.chat_id), {});
            }
        }
        catch
        {
            // The server already reported the error through the notifier.
            this.setState({ submitting: false });
        }
    }

    override render()
    {
        const inProgress = this.hasInProgressRun();
        return (
            <div className={`${Styles.ns_agent_chat_container} ${this.state.runs.length > 0 ? Styles.started : ""}`}>
                {
                    this.state.runs.map((run, idx) =>
                    {
                        const isLast = idx === this.state.runs.length - 1;
                        const defer = this.initialRunIds.has(run.id) && !isLast;
                        return (
                            <RunAgent
                                key={run.id}
                                {...this.props}
                                run={run}
                                defer={defer}
                                onRunChanged={run =>
                                {
                                    this.setState({ runs: this.state.runs.map(r => r.id === run.id ? run : r) });
                                    this.props.onRunChanged?.(run);
                                }}
                                onChatChanged={chat => this.props.onChatChanged?.(chat)}
                            />
                        );
                    })
                }

                {
                    this.state.submitting ?
                        <NSLoading
                            title="Submitting"
                            description="We are trying to run the agent ..."
                        />
                        : inProgress ?
                            null
                            :
                            <>
                                <ServerSelection {...this.props} />
                                <NSTitle
                                    title="What can i help with?"
                                    classList={[Styles.ns_agent_chat_box_title]}
                                />
                                <div className={Styles.ns_agent_chat_box_wrapper} style={{ width: "100%", maxWidth: NSBox.width.double }}>
                                    <MentionTextArea
                                        ref={this.MentionTextArea_Ref}
                                        infra={this.props.servers.infra}
                                        placeholder="Ask anything... type @ or / to mention an entity"
                                        onSubmit={this.handleSubmit}
                                    />
                                    <button
                                        type="button"
                                        onClick={this.handleSubmit}
                                        className={Styles.ns_agent_chat_submit}
                                        aria-label="Send"
                                    >
                                        Send
                                    </button>
                                </div>
                            </>
                }
            </div >
        );
    }
}

export default ChatAgent;